// @shared

// «Спросить о прогнозе»: подпись прогноза у вопроса и переходы между экраном
// прогноза и «Вопросами».

import type { ForecastPeriod, QuestionForecastRef } from './round2Types'

const PERIOD_PHRASE: Record<ForecastPeriod, string> = {
  today: 'на сегодня',
  tomorrow: 'на завтра',
  week: 'на неделю',
  month: 'на месяц',
  '3months': 'на 3 месяца',
  year: 'на год',
}

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

function parts(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) }
}

/** «20–26 окт», «28 окт – 3 ноя», «22 окт»; год — только если не текущий. */
export function forecastRefDates(ref: Pick<QuestionForecastRef, 'coversFrom' | 'coversTo'>, now = new Date()): string {
  const from = parts(ref.coversFrom)
  const to = parts(ref.coversTo) ?? from
  if (!from || !to) return ''
  const thisYear = now.getFullYear()
  // Внутри даты пробелы неразрывные — год не уезжает на новую строку.
  const yearTail = to.year !== thisYear ? `\u00a0${to.year}` : ''
  const fromMonth = MONTHS_SHORT[from.month] ?? ''
  const toMonth = MONTHS_SHORT[to.month] ?? ''
  if (from.year === to.year && from.month === to.month && from.day === to.day) {
    return `${from.day}\u00a0${fromMonth}${yearTail}`
  }
  if (from.year === to.year && from.month === to.month) {
    return `${from.day}–\u2060${to.day}\u00a0${toMonth}${yearTail}`
  }
  const fromYear = from.year !== to.year ? `\u00a0${from.year}` : ''
  return `${from.day}\u00a0${fromMonth}${fromYear} – ${to.day}\u00a0${toMonth}${yearTail}`
}

/** «К прогнозу на неделю · 20–26 окт». */
export function forecastRefLabel(ref: QuestionForecastRef, now = new Date()): string {
  const phrase = PERIOD_PHRASE[ref.period as ForecastPeriod] ?? ''
  const dates = forecastRefDates(ref, now)
  return `К прогнозу${phrase ? ` ${phrase}` : ''}${dates ? ` · ${dates}` : ''}`
}

const FORECAST_HANDOFF_KEY = 'getkarma.handoff.open-forecast.v1'

/**
 * Открыть прогноз по id: экран «Прогнозы» при входе первым читает этот
 * одноразовый ключ и тянет прогноз через /api/forecast/status — старый, уже
 * не активный тоже откроется. Ключ возобновления генерации (resume) не
 * трогается: идущий прогноз не теряется.
 */
export function rememberForecastToOpen(ref: QuestionForecastRef): boolean {
  if (typeof window === 'undefined' || !(ref.period in PERIOD_PHRASE) || !ref.forecastId) return false
  try {
    window.sessionStorage.setItem(FORECAST_HANDOFF_KEY, JSON.stringify({ forecastId: ref.forecastId, period: ref.period }))
    return true
  } catch (_) {
    return false
  }
}

/** Открыть прогноз по id и периоду без дат: тост «Прогноз готов» и ссылка из бота. */
export function rememberForecastToOpenById(target: { forecastId: string; period: string }): boolean {
  if (typeof window === 'undefined' || !(target.period in PERIOD_PHRASE) || !target.forecastId) return false
  try {
    window.sessionStorage.setItem(FORECAST_HANDOFF_KEY, JSON.stringify({ forecastId: target.forecastId, period: target.period }))
    return true
  } catch (_) {
    return false
  }
}

/** Прогноз, который просили открыть из «Вопросов»; ключ удаляется при чтении. */
export function takeForecastToOpen(): { forecastId: string; period: ForecastPeriod } | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(FORECAST_HANDOFF_KEY)
    window.sessionStorage.removeItem(FORECAST_HANDOFF_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { forecastId?: unknown; period?: unknown }
    if (typeof parsed.forecastId !== 'string' || !parsed.forecastId) return null
    if (typeof parsed.period !== 'string' || !(parsed.period in PERIOD_PHRASE)) return null
    return { forecastId: parsed.forecastId, period: parsed.period as ForecastPeriod }
  } catch (_) {
    return null
  }
}

const THREAD_HANDOFF_KEY = 'getkarma.handoff.question-thread.v1'

/** «Уточнить в Вопросах»: экран «Вопросы» при входе откроет эту нить. */
export function rememberQuestionThreadToOpen(threadId: string): void {
  if (typeof window === 'undefined' || !threadId) return
  try {
    window.sessionStorage.setItem(THREAD_HANDOFF_KEY, threadId)
  } catch (_) {}
}

export function takeQuestionThreadToOpen(): string {
  if (typeof window === 'undefined') return ''
  try {
    const threadId = window.sessionStorage.getItem(THREAD_HANDOFF_KEY) ?? ''
    window.sessionStorage.removeItem(THREAD_HANDOFF_KEY)
    return threadId
  } catch (_) {
    return ''
  }
}
